// Module 3.2a — BHA Hub.
//
// Lists every BHA owned by a single well. Reached from `/wells/:wellId/bha`.

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Plus, ArrowLeft, Trash2, Settings as Gear, Wrench,
} from "lucide-react";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { listBhasForWell, deleteBha } from "@/lib/bha/storage";
import { roleLabel } from "@/lib/bha/schema";
import { useStackMode } from "@/lib/stackModeContext";
import { stackAccentClasses, stackAccentColor } from "@/lib/stackAccent";

const ASSEMBLY_COLOR = {
  draft:    "border-zinc-500/40 text-zinc-300",
  approved: "border-emerald-500/40 text-emerald-300",
  locked:   "border-amber-500/40 text-amber-400",
};
const OPERATIONAL_COLOR = {
  planned:  "border-sky-500/40 text-sky-300",
  "in-hole":"border-amber-500/50 text-amber-400",
  racked:   "border-zinc-500/40 text-zinc-300",
  retired:  "border-zinc-700/40 text-zinc-500",
};

function Pill({ value, palette, testId }) {
  const cls = palette[value] || "border-zinc-500/40 text-zinc-400";
  return (
    <span data-testid={testId}
          className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-[0.16em] ${cls}`}>
      {value}
    </span>
  );
}

export default function BhaHub() {
  const { stackMode } = useStackMode();
  const accent = stackAccentClasses(stackMode);
  const navigate = useNavigate();
  const { wellId } = useParams();
  const [well, setWell] = useState(() => getWellProfile(wellId));
  const [rows, setRows] = useState(() => listBhasForWell(wellId));

  const refresh = () => {
    setWell(getWellProfile(wellId));
    setRows(listBhasForWell(wellId));
  };

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wellId]);

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its BHAs, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete BHA "${name}"?\n\nReference libraries and other wells are untouched.`))
      return;
    deleteBha(id);
    refresh();
  };

  return (
    <div data-testid="bha-hub" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <Link to={`/wells/${wellId}`}
              data-testid="bha-hub-back"
              className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> {well.wellName || "Well"}
        </Link>
        <Link
          to={`/wells/${wellId}/bha/new`}
          data-testid="bha-hub-new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
          style={{ background: stackAccentColor(stackMode), color: "#0A0B0E" }}>
          <Plus className="w-3.5 h-3.5" /> New BHA
        </Link>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 3.2 · {well.wellName || "Well"}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)]">
          Bottom-Hole Assemblies
        </h1>
        <p className="text-xs text-[var(--rs-text-dim,#9aa6b2)] mt-1 leading-relaxed">
          Equipment, references and geometry only. Engineering calculators
          will consume these records in later modules.
        </p>
      </div>

      {rows.length === 0 ? (
        <div data-testid="bha-hub-empty"
             className="card-rs p-6 text-center text-sm text-[var(--rs-text-dim,#9aa6b2)]">
          No BHAs yet for this well. Tap <span className={`font-medium ${accent.text}`}>+ New BHA</span> to
          design your first assembly.
        </div>
      ) : (
        <div data-testid="bha-hub-list" className="flex flex-col gap-2">
          {rows.map((b) => {
            const bit = (b.components || []).find((c) => c.role === "bit");
            const bitLabel = bit ? (bit.displayName || (bit.outerDiameter ? `${bit.outerDiameter}"` : "—")) : "—";
            return (
              <div key={b.id}
                   data-testid={`bha-row-${b.id}`}
                   className="card-rs p-3 flex items-start gap-3">
                <Link to={`/wells/${wellId}/bha/${b.id}`}
                      data-testid={`bha-open-${b.id}`}
                      className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Wrench className={`w-3.5 h-3.5 shrink-0 ${accent.text}`} aria-hidden="true" />
                    <span className="font-semibold text-sm text-[var(--rs-text,#e8edf2)] truncate">
                      {b.name || "(unnamed)"}
                    </span>
                    {b.revision && (
                      <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-zinc-500/40 text-zinc-300">
                        Rev {b.revision}
                      </span>
                    )}
                    <Pill value={b.assemblyStatus} palette={ASSEMBLY_COLOR}
                          testId={`bha-row-${b.id}-asm`} />
                    <Pill value={b.operationalStatus} palette={OPERATIONAL_COLOR}
                          testId={`bha-row-${b.id}-op`} />
                  </div>
                  <div className="mt-1 text-[11px] text-[var(--rs-text-dim,#9aa6b2)] flex items-center gap-2 flex-wrap">
                    {b.bhaNumber && <span>BHA #{b.bhaNumber}</span>}
                    {b.runNumber && <span>· Run {b.runNumber}</span>}
                    {bit && <span>· Bit {bitLabel}</span>}
                    <span>· {(b.components || []).length} component{(b.components || []).length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-3,#6b7785)]">
                    Updated {b.updatedAt ? new Date(b.updatedAt).toLocaleDateString() : "—"}
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => handleDelete(b.id, b.name)}
                  data-testid={`bha-delete-${b.id}`}
                  aria-label={`Delete ${b.name}`}
                  className="shrink-0 p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
